import ComponentList from "components/ComponentList";
import { ShowMoreBtn } from "components/List";
import { useSnackbar } from "notistack";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import apiRequest, { isErrorWithCode } from "utils/apiRequestWithErrorCode";
import Item from "./Item";
import {
  Box,
  Button,
  debounce,
  IconButton,
  InputBase,
  Typography,
} from "@material-ui/core";
import SearchIcon from "@material-ui/icons/Search";
import ClearIcon from "@material-ui/icons/Clear";
import styled from "styled-components";
import WithIconHelper from "components/WithIconHelper";
import { Skeleton } from "@material-ui/lab";
import { byLastModified } from "utils/sort";
import { SmimeCert } from "@dashboard-v3/api";

const LIST_LIMIT = 50;

export default function List() {
  const { t } = useTranslation("smime");
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState<"ALL" | "MORE">();
  const [certificates, setCertificates] = useState<SmimeCert[]>();
  const [hasMore, setHasMore] = useState<boolean>(false);
  const [searchOffset, setSearchOffset] = useState(0);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [searchInput, setSearchInput] = useState("");
  const searchTextRef = useRef("");

  useEffect(() => {
    async function initialFetch() {
      setLoading("ALL");
      await fetchCerts();
      setLoading(null);
    }

    initialFetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function fetchCerts(offset = 0, query?: string) {
    try {
      let url = `/smime/certs?offset=${offset}&limit=${LIST_LIMIT}`;
      if (query) {
        url += `&query=${encodeURIComponent(query)}`;
      }

      const res = await apiRequest<SmimeCert[]>("GET", url);

      setHasMore(res.length === LIST_LIMIT);
      setCertificates(prev => {
        if (offset === 0) return res.sort(byLastModified);
        return (prev || []).concat(res).sort(byLastModified);
      });
    } catch (e) {
      enqueueSnackbar(t(`common:errors.fetchError`), {
        variant: "error",
      });
    }
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSearch = useCallback(
    debounce(async (text: string) => {
      searchTextRef.current = text;
      setSearchOffset(0);
      setLoading("ALL");
      await fetchCerts(0, text || undefined);
      setLoading(null);
    }, 300),
    []
  );

  function handleSearchChange(value: string) {
    setSearchInput(value);
    debouncedSearch(value);
  }

  function handleClearSearch() {
    setSearchInput("");
    debouncedSearch("");
  }

  async function loadMore() {
    setLoading("MORE");
    const newOffset = searchOffset + LIST_LIMIT;
    await fetchCerts(newOffset, searchTextRef.current || undefined);
    setSearchOffset(newOffset);
    setLoading(null);
  }

  async function deleteItem(id: string) {
    try {
      setDeletingId(id);
      await apiRequest("DELETE", `/smime/certs/${id}`);

      setCertificates(prev => {
        return (prev?.filter(c => c.certificateId !== id) || []).sort(
          byLastModified
        );
      });

      enqueueSnackbar(t("delete.okMsg"), {
        variant: "success",
      });
    } catch (error) {
      if (isErrorWithCode(error) && error.code === "CERT_IN_USE") {
        enqueueSnackbar(
          t("error.CERT_IN_USE", { account: error.description["account"] }),
          { variant: "error" }
        );
      } else {
        enqueueSnackbar(t(`common:errors.fetchError`), { variant: "error" });
      }
    } finally {
      setDeletingId(null);
    }
  }

  return (
    <>
      <SearchContainer>
        <WithIconHelper position="after" text={<SearchHelpContent />}>
          <InputBase
            fullWidth
            name="search"
            placeholder={t("search.placeholder")}
            value={searchInput}
            onChange={e => handleSearchChange(e.target.value)}
            startAdornment={
              searchInput ? (
                <IconButton size="small" onClick={handleClearSearch}>
                  <ClearIcon />
                </IconButton>
              ) : (
                <SearchIcon color="disabled" />
              )
            }
          />
        </WithIconHelper>
      </SearchContainer>
      <ComponentList
        list={certificates}
        loading={loading === "ALL"}
        emptyMsg={<EmptyList />}
        renderItems={certificate =>
          deletingId === certificate.certificateId ? (
            <Skeleton variant="rect" height={116} />
          ) : (
            <Item
              key={certificate.certificateId}
              certificate={certificate}
              onDelete={() => deleteItem(certificate.certificateId)}
            />
          )
        }
      />
      {hasMore && (
        <ShowMoreBtn loading={loading === "MORE"} onClick={loadMore} />
      )}
    </>
  );
}

const SearchContainer = styled.div`
  padding: 5px 10px;
  border-radius: 4px;
  background-color: rgb(224, 231, 239);
  margin-top: 14px;

  input {
    padding-left: 10px;
  }

  svg {
    font-size: 18px;
  }
`;

function SearchHelpContent() {
  const { t } = useTranslation("smime");
  return (
    <Box p=".5rem .8rem">
      <Typography variant="subtitle2">{t("search.helpTitle")}</Typography>
      <ul style={{ margin: "8px 0 0", paddingLeft: 20 }}>
        <li>{t("form.nameLabel")}</li>
        <li>{t("certDetails.subject")}</li>
        <li>{t("certDetails.issuer")}</li>
        <li>{t("certDetails.serial")}</li>
        <li>{t("certDetails.emails")}</li>
      </ul>
    </Box>
  );
}

function EmptyList() {
  const { t } = useTranslation("smime");

  return (
    <Box p="10px">
      <Typography>{t("emptyMsg")}</Typography>
      <Box mt="20px">
        <Button
          variant="contained"
          color="primary"
          disableElevation
          href="/smime-certs/new"
        >
          {t("emptyAction")}
        </Button>
      </Box>
    </Box>
  );
}
